import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AutoAwesome as QualitativeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import {
  SMART_STATUS_COLORS,
  formatSigned,
  getEntityBreakdown,
  getSmartColor,
  getSmartStatusForScore,
  toNumber
} from './editorialIntelligenceUtils';

/**
 * Componente injetado na Barra Lateral (Sidebar) para mostrar a nota resumida.
 * Se houver concorrência, mostra o Cabo de Guerra. Se não, mostra o SmartScore.
 */
const SidebarQualitativeCTA = ({ article, onClick, onRecalculate }) => {
  const [recalculating, setRecalculating] = React.useState(false);

  const handleRecalculate = async (e) => {
    e.stopPropagation();
    if (recalculating || !article?._id || !onRecalculate) return;
    setRecalculating(true);
    try {
      const res = await api.put(`/articles/${article._id}/smart-score`);
      if (res.data?.success && onRecalculate) {
        onRecalculate(res.data.data);
      }
    } catch (err) {
      console.error('[SidebarQualitativeCTA] Erro ao recalcular SmartScore:', err);
    } finally {
      setRecalculating(false);
    }
  };
  // Extrai dados
  const smartScore = article?.smartScore || {};
  const hasSmartScore = smartScore.calculatedAt != null;
  const entities = getEntityBreakdown(article);
  const brandEntity = entities.find(e => e.type === 'brand');
  const competitors = entities.filter(e => e.type !== 'brand');
  const brandLabel = brandEntity?.name || article?.brandName || 'Marca';
  const competitorLabel = competitors.length === 1
    ? competitors[0]?.name || 'Concorrente'
    : 'Concorrentes';
  
  const hasCompetitorScore = article?.competitorScore?.calculatedAt != null;
  const competitorScore = article?.competitorScore || {};
  
  // O Share Qualitativo só faz sentido se houver pelo menos duas marcas/entidades competindo
  const isCompetitive = entities.length > 1 && competitors.length > 0 && hasCompetitorScore;
  
  let label = "Analisar Publicação";
  let value = "--";
  let status = "Não calculado";
  let color = "#607D8B";
  let percent = 0;
  let gradient = '';
  let dotColor = '';
  let showBar = false;
  let isCompetitionBar = false;

  const getProgressParams = (scoreVal) => {
    const s = toNumber(scoreVal);
    const isNegative = s < 0;
    const absScore = Math.abs(s);
    const pct = Math.min(100, (absScore / 10) * 100);
    const grad = isNegative 
      ? 'linear-gradient(90deg, rgba(158,158,158,0.3) 0%, #ffd54f 30%, #ffb74d 50%, #e57373 75%, #d32f2f 100%)' 
      : 'linear-gradient(90deg, rgba(158,158,158,0.3) 0%, #dce775 30%, #aed581 50%, #81c784 75%, #4caf50 100%)';
    const dotC = isNegative ? '#f44336' : (s > 0 ? '#4caf50' : '#78909C');
    return { pct, grad, dotC };
  };

  const getCompetitionProgressParams = (scoreVal) => {
    const s = Math.max(-10, Math.min(10, toNumber(scoreVal)));
    return {
      pct: ((s + 10) / 20) * 100,
      grad: 'linear-gradient(90deg, #f44336 0%, #ffb74d 24%, #9e9e9e 50%, #aed581 76%, #4caf50 100%)',
      dotC: s < 0 ? '#f44336' : (s > 0 ? '#4caf50' : '#78909C')
    };
  };

  if (isCompetitive) {
    label = "Share Qualitativo (SmartScore)";
    value = formatSigned(competitorScore.score);
    status = competitorScore.status || 'Empate';
    
    // Config de cor da disputa
    const s = toNumber(competitorScore.score);
    if (s > 2) color = '#2e7d32';
    else if (s < -2) color = '#c62828';
    else color = '#1565c0';
    const params = getCompetitionProgressParams(competitorScore.score);
    percent = params.pct;
    gradient = params.grad;
    dotColor = params.dotC;
    showBar = true;
    isCompetitionBar = true;
  } else if (hasSmartScore) {
    label = "SmartScore";
    // Se for apenas concorrente (1 entidade), usamos a nota do artigo/concorrente que está no SmartScore principal
    value = formatSigned(smartScore.score);
    status = getSmartStatusForScore(smartScore.score);
    color = SMART_STATUS_COLORS[status] || getSmartColor(smartScore);
    const params = getProgressParams(smartScore.score);
    percent = params.pct;
    gradient = params.grad;
    dotColor = params.dotC;
    showBar = true;
  }

  // Verifica contraste da cor de fundo para ajustar as cores do texto
  const getContrastMode = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return false;
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 150;
  };
  
  const isLightBg = getContrastMode(color);
  const textColor = isLightBg ? 'rgba(0,0,0,0.85)' : '#fff';
  const mutedTextColor = isLightBg ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)';
  const buttonBg = isLightBg ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.2)';
  const buttonHoverBg = isLightBg ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.3)';

  return (
    <Box sx={{
      mb: 2,
      p: 1.5,
      borderRadius: '8px',
      bgcolor: color,
      border: 'none'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: mutedTextColor, textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 950, color: `${textColor} !important` }}>
              {value}
            </Typography>
            {hasSmartScore && (
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: `${textColor} !important`, mt: 0.5 }}>
                {status}
              </Typography>
            )}
          </Box>
          
          {showBar && (
            <Box sx={{ mt: 1.2, mb: 0.7 }}>
              {isCompetitionBar && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', alignItems: 'end', columnGap: 0.75, mb: 0.45 }}>
                  <Typography
                    title={competitorLabel}
                    sx={{
                      fontSize: '0.52rem',
                      fontWeight: 850,
                      color: mutedTextColor,
                      textTransform: 'uppercase',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {competitorLabel}
                  </Typography>
                  <Typography
                    title={brandLabel}
                    sx={{
                      fontSize: '0.52rem',
                      fontWeight: 850,
                      color: mutedTextColor,
                      textTransform: 'uppercase',
                      textAlign: 'right',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {brandLabel}
                  </Typography>
                </Box>
              )}
              <Box sx={{ position: 'relative', height: 10, mx: '6px', width: 'calc(100% - 12px)' }}>
              {/* Trilho de fundo visível */}
              <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 4, borderRadius: 99, bgcolor: isLightBg ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.18)' }} />
              {/* Gradiente 0→10 em cima */}
              <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 4, borderRadius: 99, overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, background: gradient, opacity: 1 }} />
              </Box>
              {isCompetitionBar && (
                <Box sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 2,
                  height: 12,
                  borderRadius: 99,
                  transform: 'translate(-50%, -50%)',
                  bgcolor: isLightBg ? 'rgba(0,0,0,0.42)' : 'rgba(255,255,255,0.72)',
                  zIndex: 1
                }} />
              )}
              <Box sx={{
                position: 'absolute',
                left: `${percent}%`,
                top: '50%',
                width: 10,
                height: 10,
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: dotColor,
                border: '2px solid #fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                zIndex: 2
              }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Botão de Atualizar SmartScore */}
        {onRecalculate && article?._id && (
          <Tooltip title="Atualizar SmartScore" arrow placement="top">
            <IconButton
              size="small"
              onClick={handleRecalculate}
              disabled={recalculating}
              sx={{
                p: 0.5,
                color: isLightBg ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.7)',
                flexShrink: 0,
                mt: 0.25,
                '&:hover': {
                  bgcolor: isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'
                }
              }}
            >
              {recalculating
                ? <CircularProgress size={14} sx={{ color: isLightBg ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.7)' }} />
                : <RefreshIcon sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Button
        fullWidth
        variant="contained"
        startIcon={<QualitativeIcon sx={{ fontSize: 16 }} />}
        onClick={onClick}
        sx={{
          bgcolor: buttonBg,
          color: textColor,
          fontWeight: 800,
          borderRadius: '6px',
          textTransform: 'none',
          py: 0.8,
          boxShadow: 'none',
          '&:hover': { bgcolor: buttonHoverBg, boxShadow: 'none' },
        }}
      >
        Ver Análise Qualitativa
      </Button>
    </Box>
  );
};

SidebarQualitativeCTA.propTypes = {
  article: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func
};

export default SidebarQualitativeCTA;
